import React, { useState, useEffect, useRef, useCallback } from 'react';
import { partyAPI } from '../services/api';
import { Plus, Search } from 'lucide-react';

export default function PartyAutocomplete({ value, onChange, onPartySelect, placeholder = "Search party name...", className = "", partyType = "", allowCreate = true }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    // Cancel previous request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = {};
      if (q && q.length >= 1) params.search = q;
      if (partyType) params.type = partyType;
      const res = await partyAPI.list(params);
      if (controller.signal.aborted) return;
      // Backend returns array directly from /parties endpoint
      const data = Array.isArray(res.data) ? res.data : (res.data?.parties || res.data || []);
      setResults(data);
      setOpen(true);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') setResults([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [partyType]);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    onChange(q);
    clearTimeout(debounceRef.current);
    // Fast debounce: 150ms for responsive feel
    debounceRef.current = setTimeout(() => doSearch(q), 150);
  };

  const selectParty = (p) => {
    setQuery(p.name);
    onChange(p.name);
    if (onPartySelect) onPartySelect(p);
    setOpen(false);
  };

  const createAndSelect = async () => {
    try {
      const res = await partyAPI.create({ name: query, type: partyType || 'general' });
      setQuery(res.data.name);
      onChange(res.data.name);
      if (onPartySelect) onPartySelect(res.data);
      setOpen(false);
    } catch { }
  };

  const exactMatch = results.some(p => p.name.toLowerCase() === query.toLowerCase());

  // Highlight matching text in results
  const highlightMatch = (name) => {
    if (!query) return name;
    const idx = name.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return name;
    return (
      <>
        {name.slice(0, idx)}
        <span className="bg-yellow-100 text-yellow-800 font-semibold">{name.slice(idx, idx + query.length)}</span>
        {name.slice(idx + query.length)}
      </>
    );
  };

  return (
    <div ref={ref} className="relative" data-testid="party-autocomplete">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => doSearch(query)}
          placeholder={placeholder}
          className={`form-input pl-8 ${className}`}
          data-testid="party-autocomplete-input"
          autoComplete="off"
        />
        {loading && <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />}
      </div>
      {open && (results.length > 0 || (allowCreate && query.length > 0)) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-56 overflow-y-auto" data-testid="party-autocomplete-dropdown">
          {results.length > 0 && (
            <div className="px-2.5 py-1.5 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100 bg-slate-50 sticky top-0">
              {results.length} {results.length === 1 ? 'match' : 'matches'} found
            </div>
          )}
          {results.map((p) => (
            <button
              key={p.party_id}
              type="button"
              onClick={() => selectParty(p)}
              className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm border-b border-slate-50 last:border-0 transition-colors"
              data-testid={`party-option-${p.party_id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800">{highlightMatch(p.name)}</span>
                {p.type && p.type !== 'general' && (
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${p.type === 'customer' ? 'bg-blue-50 text-blue-600' : p.type === 'vendor' ? 'bg-orange-50 text-orange-600' : 'bg-slate-100 text-slate-500'}`}>
                    {p.type}
                  </span>
                )}
              </div>
              {(p.gstin || p.address || p.state) && (
                <div className="flex gap-2 mt-0.5 text-[11px] text-slate-400">
                  {p.gstin && <span>GSTIN: {p.gstin}</span>}
                  {p.state && <span>{p.state}</span>}
                  {!p.gstin && p.address && <span className="truncate max-w-[200px]">{p.address}</span>}
                </div>
              )}
            </button>
          ))}
          {results.length === 0 && !loading && query.length > 0 && (
            <div className="px-3 py-3 text-sm text-slate-400 text-center">No parties matching "{query}"</div>
          )}
          {allowCreate && !exactMatch && query.length > 0 && !loading && (
            <button
              type="button"
              onClick={createAndSelect}
              className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 text-sm font-medium text-emerald-700 flex items-center gap-2 border-t border-slate-100 sticky bottom-0 bg-white"
              data-testid="party-create-new"
            >
              <Plus className="w-3.5 h-3.5" /> Create "{query}" as new party
            </button>
          )}
        </div>
      )}
    </div>
  );
}
